import { ForgotPasswordForm as DynamicForgotPasswordForm } from '@Auth/components/ForgotPasswordForm';
import { PageAuthContext } from '@Pages/pageRuntime/context/PageAuthContext';
import { usePageRouter } from '@Pages/pageRuntime/pageRuntimeContextValue';
import type React from 'react';
import { useContext } from 'react';

type ForgotPasswordFormProps = {
  /**
   * The authenticatable resource whose password is being reset. Defaults to the
   * workspace's sole authenticatable resource; required only when the workspace has
   * more than one.
   */
  resourceId?: string;

  /** Prefills the email field. */
  initialIdentifier?: string;

  /** Label for the identifier (email) field. */
  identifierLabel?: React.ReactNode;

  /** Placeholder for the identifier (email) field. */
  identifierPlaceholder?: string;

  /** Contents of the submit button. */
  submitLabel?: React.ReactNode;

  /** Additional classes for the form element. */
  className?: string;

  /** Additional classes for the submit button. */
  buttonClassName?: string;

  /**
   * Where the sign-in that completes the reset continues when it needs two-factor.
   * `'hosted'`, the default, uses Diagonal's challenge page. `'inline'` returns to this
   * page to show the challenge, so render `Sdk.Components.TwoFactorChallenge` and
   * `Sdk.Components.TwoFactorSetup` here too.
   */
  twoFactor?: 'inline' | 'hosted';

  /** With `'current'`, the visitor returns to this page after the reset signs them in. */
  returnTo?: 'current';
};

/**
 * A themeable form that emails a password reset link for the workspace's authenticatable
 * resource. `Sdk.Components.LoginForm` already offers this inline behind its "Forgot
 * password?" control — reach for this component only when the reset step belongs on its
 * own page. Rate limiting and token issuing are handled server-side, and the response is
 * identical whether or not an account exists.
 *
 * @example
 * <Sdk.Components.ForgotPasswordForm />
 */
export function ForgotPasswordForm({
  resourceId,
  initialIdentifier,
  identifierLabel,
  identifierPlaceholder,
  submitLabel,
  className,
  buttonClassName,
  twoFactor = 'hosted',
  returnTo
}: ForgotPasswordFormProps) {
  const authConfig = useContext(PageAuthContext);
  const router = usePageRouter();

  const targetResourceId = resourceId ?? authConfig?.resourceId ?? null;

  if (!targetResourceId) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
        No authenticatable resource is configured for this workspace. Pass a `resourceId` prop to
        `Sdk.Components.ForgotPasswordForm`.
      </div>
    );
  }

  return (
    <DynamicForgotPasswordForm
      resourceId={targetResourceId}
      initialIdentifier={initialIdentifier}
      identifierLabel={identifierLabel}
      identifierPlaceholder={identifierPlaceholder}
      submitLabel={submitLabel}
      className={className}
      buttonClassName={buttonClassName}
      twoFactor={twoFactor === 'inline' ? 'inline' : undefined}
      returnTo={returnTo}
      currentPageId={authConfig?.currentPageId}
      router={router}
    />
  );
}
